import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import type { CustomColumn } from 'lumen/components/hotel/pricing-assistant/table/cells/day-table/th-day-table-value-cell';
import type { TablePricingManagerCustomRow } from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import TooltipOwnRateCell from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-own-rate-cell';
import type { Carate } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

import styles from './td-own-rate.module.css';

interface HotelPricingAssistantTableCellsTdOwnRateSignature {
  Args: {
    column: CustomColumn;
    row: TablePricingManagerCustomRow;
    value: Carate;
  };
}

export default class HotelPricingAssistantTableCellsTdOwnRate extends Component<HotelPricingAssistantTableCellsTdOwnRateSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get carate() {
    return this.args.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get carecommendation() {
    return this.carate.carecommendation;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.column.date);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRecommendationAndOwnRateDifferencePositive() {
    return this.carecommendation
      ?.formattedDifferenceBetweenRecommendationAndOwnRate
      ?.isPositiveDifference;
  }

  get showPriceOptimisedIcon(): boolean {
    return this.carate.hasValue && !this.carecommendation && !this.isPast;
  }

  get showRecommendation(): boolean {
    return !this.isPast && Boolean(this.carecommendation);
  }

  @action
  onDoubleClick(): void {
    if (this.access.isStaffUser && console) {
      const dateStr = this.args.column.date.format('YYYY-MM-DD');
      const hotelName = this.carate?.hotelinfo.name;
      const { roomName } = this.args.row.room;

      console.group(
        `Debug: Pricing Manager Recommendation Cell - ${hotelName} - ${roomName} - ${dateStr}`,
      );

      if (console.table && this.carate) {
        console.log('Carate object:');
        console.table(this.carate.toJSON());
      } else {
        console.log('No console.table available or carate is undefined');
      }

      if (this.carecommendation) {
        console.log('Carecommendation object:');
        if (console.table) {
          console.table(this.carecommendation.toJSON());
        }
      } else {
        console.log('No Carecommendation for this Carate.');
      }

      console.groupEnd();
    }
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div
      class="flex flex-col truncate tabular-nums"
      {{on "dblclick" this.onDoubleClick}}
    >
      {{#if this.showRecommendation}}
        <div
          class="text-300--regular flex justify-end gap-100
            {{if this.isPast 'text-disabled'}}"
        >
          {{this.carecommendation.formattedPriceToShow}}
          <PrismIcon
            @color={{if
              this.isRecommendationAndOwnRateDifferencePositive
              "on-success"
              "on-critical"
            }}
            @name={{if
              this.isRecommendationAndOwnRateDifferencePositive
              "arrow-up"
              "arrow-down"
            }}
            @size="small"
          />
        </div>
      {{/if}}
      <div
        class="text-200--regular flex justify-end gap-100
          {{if this.isPast 'text-disabled' 'text-neutral-subdued'}}"
      >
        {{this.carate.formattedRate}}
        <div class={{styles.cell-icon-block}}>
          {{#if this.showPriceOptimisedIcon}}
            <PrismIcon @color="on-success" @name="checkmark" @size="small" />
          {{/if}}
        </div>
      </div>
      <TooltipOwnRateCell @carate={{this.carate}} />
    </div>
  </template>
}
